import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { BehaviorSubject, combineLatest, distinctUntilChanged } from 'rxjs';
import { map, take, withLatestFrom } from 'rxjs/operators';

import { AccountSelectors, CurrentUserSelectors } from '@core/account-data';
import { filterBoolean, Helpers, LocalStorageService } from '@core/common-legacy';
import { injectSubscriptionStatusContract } from '@core/company-contracts';
import type { Company } from '@core/company-types';
import { FeatureGuardFacade, GuardedFeature } from '@core/feature-guard-legacy';
import { GettingStartedActions, GettingStartedSelectors } from '@core/getting-started-data';
import type { GettingStartedSubtask } from '@core/getting-started-types';
import { SpamVerificationService } from '@core/spam-verification-logic';

import { GettingStartedAnalyticsService } from './getting-started-analytics.service';

@Injectable({
  providedIn: 'root',
})
export class GettingStartedFacade {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly spamVerificationService = inject(SpamVerificationService);
  private readonly gettingStartedAnalyticsService = inject(GettingStartedAnalyticsService);
  private readonly featureGuardFacade = inject(FeatureGuardFacade);
  private readonly router = inject(Router);

  protected readonly subscription = injectSubscriptionStatusContract();

  public readonly SUBTASK_IN_PROGRESS: string = 'subtask_in_progress';
  public readonly FIRST_TIME_CLOSED_TOOLTIP = 'first_time_closed_tooltip';

  public showAcademyButton$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public showAcademyButton: boolean = false;
  public featuresAvailable: { isRolesAvailable: boolean; isPipelinesAvailable: boolean };

  private currentCompany: Company;

  constructor() {
    this.store
      .select(GettingStartedSelectors.showAcademyButtonSelector)
      .subscribe(showAcademyButton => {
        this.showAcademyButton = showAcademyButton;
        this.showAcademyButton$.next(showAcademyButton);
      });

    this.store
      .select(AccountSelectors.currentCompany)
      .pipe(filterBoolean, withLatestFrom(this.store.select(CurrentUserSelectors.data)))
      .subscribe(([currentCompany]) => {
        this.currentCompany = currentCompany;
      });
    combineLatest([
      this.featureGuardFacade.checkFeatureAvailability(GuardedFeature.AdjustableRoles),
      this.featureGuardFacade.checkFeatureAvailability(GuardedFeature.Pipelines),
    ])
      .pipe(take(1))
      .subscribe(([isRolesAvailable, isPipelinesAvailable]) => {
        this.featuresAvailable = { isRolesAvailable, isPipelinesAvailable };
      });

    this.checkGettingStartedCompletionState();
  }

  public markSubtaskAsDone(subtask: GettingStartedSubtask) {
    this.gettingStartedAnalyticsService.trackGetStarted(subtask, 'completed');
    this.store.dispatch(
      GettingStartedActions.update({ payload: { [Helpers.camelize(subtask)]: true } }),
    );
  }

  public hideQuickStartButton(): void {
    const companyId = this.currentCompany?.id;
    if (companyId && this.showAcademyButton) {
      this.store.dispatch(GettingStartedActions.toggleAcademyButton({ payload: false }));

      this.actions$
        .pipe(ofType(GettingStartedActions.toggleAcademyButtonSuccess), take(1))
        .subscribe(() => {
          this.showAcademyButton$.next(false);
        });
    }
  }

  public getProgressState(): Observable<[number, boolean]> {
    return combineLatest([
      this.store.select(GettingStartedSelectors.percentageProgressSelector),
      this.showAcademyButton$.pipe(distinctUntilChanged()),
    ]);
  }

  public goToPlanPicker() {
    this.router.navigate([{ outlets: { dialog: ['order-2024', 'select-product'] } }]);
  }

  public setSubtaskInProgress(subtask: GettingStartedSubtask): void {
    this.localStorageService.set(this.SUBTASK_IN_PROGRESS, subtask);
  }

  public getSubtaskInProgress(): GettingStartedSubtask {
    return this.localStorageService.get(this.SUBTASK_IN_PROGRESS);
  }

  public setFirstTimeCloseTooltip(): void {
    this.localStorageService.set(this.FIRST_TIME_CLOSED_TOOLTIP, true);
  }

  public getFirstTimeCloseTooltip(): boolean {
    return this.localStorageService.get(this.FIRST_TIME_CLOSED_TOOLTIP);
  }

  public canAccessToGettingStarted(): Observable<boolean> {
    return combineLatest([
      this.store.select(AccountSelectors.currentCompany),
      this.spamVerificationService.signupDomainVerifiedAt$,
    ]).pipe(
      filterBoolean,
      map(([currentCompany, isAccountVerified]) =>
        [
          !this.subscription.isTrialEnded(),
          !this.subscription.isExpired(),
          !this.subscription.isBlocked(),
          isAccountVerified,
        ].every(Boolean),
      ),
    );
  }

  private checkGettingStartedCompletionState(): void {
    this.getProgressState()
      .pipe(takeUntilDestroyed())
      .subscribe(([progress, showAcademyButton]) => {
        if (progress === 100 && showAcademyButton) {
          this.hideQuickStartButton();
        }
      });
  }
}
